import { List } from '@material-ui/core';
import React, { useState } from 'react';
import utils from 'utils';
import { Values, Key, OpenLink, Filter } from './styles';
import { KpiCategoryFilterConfiguration, KpiCategoryFilterValue } from 'models/request';

export interface FilterListProps {
  dateFrom?: string;
  dateTo?: string;
  gbsList?: string[];
  channels?: string[];
  types?: string[];
  companies?: string[];
  showSummaryDetails?: boolean;
  categoryName?: string;
  dateLabel?: string | null;
  kpiCategoryFilterValues?: KpiCategoryFilterValue[];
  kpiCategoryFilterConfig?: KpiCategoryFilterConfiguration[];
}

const ListElement = ({ list }: { list: string[] }) => {
  const t = utils.intl.useTranslator();

  const [isOpen, setIsOpen] = useState(false);

  const max = 10;
  const shortList = list?.slice(0, max);

  const formatList = (list: string[]) => list.map((el, index) => (index === list.length - 1 ? el + ' ' : el + ', '));

  const showShortList = list?.length > max && !isOpen;

  return (
    <Values>
      {formatList(showShortList ? shortList : list)}
      {list.length > max && (
        <OpenLink onClick={() => setIsOpen(!isOpen)}>
          {isOpen
            ? utils.app.textTranslated('hide', t)
            : `+${list.length - max} ${utils.app.textTranslated('more', t)}`}
        </OpenLink>
      )}
    </Values>
  );
};

const FilterList = (props: FilterListProps) => {
  const t = utils.intl.useTranslator();

  const {
    dateFrom,
    dateTo,
    gbsList,
    channels,
    types,
    companies,
    showSummaryDetails,
    categoryName,
    dateLabel,
    kpiCategoryFilterValues,
    kpiCategoryFilterConfig,
  } = props;

  return (
    <List>
      {showSummaryDetails && (
        <Filter>
          <Key>
            {categoryName} {utils.app.textTranslated('ct-filterlist-to-date', t, 'to Date')}:
          </Key>
          <Values>{dateTo}</Values>
        </Filter>
      )}

      {dateFrom && dateTo && (
        <Filter>
          <Key>{dateLabel ? utils.app.textTranslated(dateLabel, t) : 'Date'}:</Key>
          <Values>
            {dateFrom} - {dateTo}
          </Values>
        </Filter>
      )}

      {gbsList && (
        <Filter>
          <Key>{utils.app.textTranslated('ct-filterlist-gbs', t, 'Gbs')}:</Key>
          <ListElement list={gbsList} />
        </Filter>
      )}

      {companies && companies.length > 0 && (
        <Filter>
          <Key>{utils.app.textTranslated('ct-filterlist-company', t, 'Company')}:</Key>
          <ListElement list={companies} />
        </Filter>
      )}

      {types && types.length > 0 && (
        <Filter>
          <Key>{utils.app.textTranslated('ct-filterlist-type', t, 'Type')}:</Key>
          <ListElement list={types} />
        </Filter>
      )}

      {channels && channels.length > 0 && (
        <Filter>
          <Key>{utils.app.textTranslated('ct-filterlist-channel', t, 'Channel')}:</Key>
          <ListElement list={channels} />
        </Filter>
      )}

      {!!kpiCategoryFilterValues?.length && (
        <>
          {kpiCategoryFilterValues.map((f) => {
            const filterConfig = kpiCategoryFilterConfig?.find((c) => c.filterCode === f.filterCode);
            const label = filterConfig?.label ?? f.filterCode;
            const options = filterConfig?.options ?? [];

            const resolveLabel = (value: string): string => {
              const exact = options.find((o) => o.value === value);
              if (exact) return exact.label;
              const bySuffix = options.find((o) => o.value.split('-').pop() === value);
              return bySuffix?.label ?? value;
            };

            let displayValues: string[] | null = null;

            if (f.multipleValues) {
              displayValues = f.multipleValues.map(resolveLabel);
            } else if (f.rangeStartValue && f.rangeEndValue) {
              displayValues = [
                `${utils.date.convertDate(f.rangeStartValue)} - ${utils.date.convertDate(f.rangeEndValue)}`,
              ];
            } else if (f.singleValue) {
              displayValues = [f.singleValue];
            }

            if (!displayValues) return null;

            return (
              <Filter key={f.filterCode}>
                <Key>{label}:</Key>
                <ListElement list={displayValues} />
              </Filter>
            );
          })}
        </>
      )}
    </List>
  );
};

export default FilterList;
